import React, { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { withStyles } from '@mui/styles';
import Switch from '@mui/material/Switch';
import { checkHasCompanyFeature, checkCompanyCanBeUpgraded } from 'helper/commonFunctions';
import InfoTooltip from 'components/tooltips/InfoTooltip';
import Zendesk from 'helper/zendeskFunctions';
import { appCtx } from 'components/appStore';
import SubscriptionsLink from 'components/subscription/SubscriptionsLink';
import DeadlineForm from 'components/assessments_pages/asessment_settings_components/DeadlineForm';
import EditPanelSection from '../EditPanelSection';

import styles from './styles';

const MIN_DEADLINE = 1;
const MAX_DEADLINE = 29;
const DEFAULT_DEADLINE = 4;

const DeadlineSettings = observer((props) => {
    const { changeAssessmentSettings, setAudition, audition, settings, classes } = props;
    const [deadlineType, setDeadlineType] = useState(null); // 'days', 'date' or null
    const { company } = useContext(appCtx);

    const hasDeadlineFeature = checkHasCompanyFeature(company, 'CHANGE_AUDITION_DEADLINE');

    useEffect(() => {
        if (settings.daysToExpiration !== -1) setDeadlineType('days');
        else if (settings.expirationDate) setDeadlineType('date');
    }, [settings]);

    const onDaysChange = (newValue) => {
        changeAssessmentSettings({ daysToExpiration: newValue, expirationDate: null })
            .then(() => setAudition({ ...audition, daysToExpiration: newValue, expirationDate: null }));
    };

    const onChangeDeadlineDate = (newDate, isExpired) => {
        if (!newDate) return;

        changeAssessmentSettings({
            daysToExpiration: -1,
            expirationDate: newDate
        }).then(() => {
            setAudition({
                ...audition,
                daysToExpiration: -1,
                expirationDate: newDate,
                expired: isExpired
            });
        });
    };

    const handleToggleDeadline = (_, checked) => {
        const enable = checked;
        const newDaysToExpiration = enable ? DEFAULT_DEADLINE : -1;
        changeAssessmentSettings({ daysToExpiration: newDaysToExpiration, expirationDate: null })
            .then(() => {
                setAudition({ ...audition, daysToExpiration: newDaysToExpiration, expired: false });
                setDeadlineType(enable ? 'days' : null);
            });
    };

    const onChangeDeadlineType = (type, expirationDate = null) => {
        setDeadlineType(type);

        if (type === 'days') {
            changeAssessmentSettings({
                daysToExpiration: DEFAULT_DEADLINE,
                expirationDate: null
            }).then(() => {
                setAudition({ ...audition, daysToExpiration: DEFAULT_DEADLINE, expirationDate: null, expired: false });
            });
        }

        if (type === 'date') {
            if (expirationDate) {
                changeAssessmentSettings({
                    daysToExpiration: -1,
                    expirationDate
                }).then(() => {
                    setAudition({ ...audition, expirationDate, daysToExpiration: -1 });
                });
            } else {
                setAudition({ ...audition, expirationDate, daysToExpiration: -1 });
            }
        }
    };

    const openZendesk = () => {
        Zendesk.open(true);
    };

    const { isAtsEnabled } = company;
    const { apiIntegration } = company.companySettings;

    if (settings.daysToExpiration < -1) return null;

    const renderDeadlineForm = () => (
        <DeadlineForm
            {...props}
            disabled={!hasDeadlineFeature}
            initDays={settings.daysToExpiration > -1 ? settings.daysToExpiration : DEFAULT_DEADLINE}
            initType={deadlineType}
            initDate={settings.expirationDate}
            min={MIN_DEADLINE}
            max={MAX_DEADLINE}
            defaultDeadline={DEFAULT_DEADLINE}
            onDaysChange={onDaysChange}
            onChangeDeadlineType={onChangeDeadlineType}
            onChangeDeadlineDate={onChangeDeadlineDate}
            description="Candidate deadline for submission from the moment they apply or are invited."
        />
    );

    if (isAtsEnabled) {
        return (
            <EditPanelSection
                className={classes.wrapper}
                header={(
                    <>
                        Deadline
                        <InfoTooltip
                            id="integrations-deadline"
                            isBlack
                            placement="left"
                            text="Deadlines are deactivated when using the PageUp integration. Use the PageUp interface to customize Skills Assessment deadlines."
                        />
                    </>
                )}
            >
                <p className="u-mrg--tx3">No deadline</p>
            </EditPanelSection>
        );
    }

    if (hasDeadlineFeature) {
        return (
            <EditPanelSection
                className={classes.wrapper}
                header={(
                    <>
                        Set Deadline
                        <div>
                            <InfoTooltip
                                id="integrations-deadline"
                                isBlack
                                placement="left"
                                text="Set a custom deadline for candidates to complete their Skills Assessment by."
                            />
                            {apiIntegration && (
                                <Switch
                                    checked={Boolean(deadlineType)}
                                    onChange={handleToggleDeadline}
                                />
                            )}
                        </div>
                    </>
                )}
            >
                {renderDeadlineForm()}
            </EditPanelSection>
        );
    }

    return (
        <EditPanelSection
            className={classes.wrapper}
            header={(
                <>
                    Deadline
                    <InfoTooltip
                        id="integrations-deadline"
                        isBlack
                        placement="left"
                        text="Set a custom deadline for candidates to complete their Skills Assessment by."
                    />
                </>
            )}
        >
            {renderDeadlineForm()}
            <p className={classes.labelStyle}>Want to enable custom deadlines?&nbsp;
                {
                    checkCompanyCanBeUpgraded(company)
                        ? <SubscriptionsLink label="Upgrade account now" className={classes.talkToUsLink} segmentLocation="custom deadlines for candidates field" />
                        : <span role="presentation" className={classes.talkToUsLink} onClick={openZendesk}>Talk to us.</span>
                }
            </p>
        </EditPanelSection>
    );
});

export default withStyles(styles)(DeadlineSettings);
